import { useEffect, useRef, useState } from 'react'
import classes from './ChatWidget.module.scss'
import { FEEDBACK_COMMENT_MAX, FEEDBACK_REASONS, nextVote } from './messageFeedback'
import { FeedbackVote } from './types'

interface Props {
  vote: FeedbackVote
  /** Sunucuya yazar. Reddederse bileşen iyimser durumu geri alır (oy) ya da paneli açık tutar (ayrıntı). */
  onVote: (vote: FeedbackVote, reasonCode?: string, comment?: string) => Promise<void>
}

const THANKS_MS = 2000

/**
 * Yanıt altındaki oy çubuğu (ADR-015, spec §6.1). Olumlu oy TEK dokunuştur. Olumsuz oy hemen
 * yazılır; ardından satır içi panel açılır: tek seçimli, geri alınabilir neden çipleri + en fazla
 * 500 karakterlik isteğe bağlı not. Çiplere tıklamak göndermez; seçili neden ve not tek "Gönder"
 * isteğinde gider. `×` hiçbir şey göndermeden kapatır (oy `down` kalır).
 */
export default function FeedbackBar({ vote, onVote }: Props) {
  const [current, setCurrent] = useState<FeedbackVote>(vote)
  const [askReason, setAskReason] = useState(false)
  const [reasonCode, setReasonCode] = useState<string | null>(null)
  const [comment, setComment] = useState('')
  const [detailError, setDetailError] = useState(false)
  const [thanks, setThanks] = useState(false)
  const [busy, setBusy] = useState(false)
  const thanksTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Geçmişten yeni bir oy geldiğinde (balon yeniden kullanıldığında) durumu senkronla.
  useEffect(() => setCurrent(vote), [vote])

  useEffect(
    () => () => {
      if (thanksTimerRef.current) clearTimeout(thanksTimerRef.current)
    },
    []
  )

  const showThanks = () => {
    setThanks(true)
    if (thanksTimerRef.current) clearTimeout(thanksTimerRef.current)
    thanksTimerRef.current = setTimeout(() => setThanks(false), THANKS_MS)
  }

  const closePanel = () => {
    setAskReason(false)
    setReasonCode(null)
    setComment('')
    setDetailError(false)
  }

  /** 👍 / 👎 düğmeleri: oy iyimser yazılır, hata olursa geri alınır. */
  const send = async (target: FeedbackVote) => {
    if (busy) return
    const previous = current

    setCurrent(target)
    setBusy(true)
    try {
      await onVote(target)
      // Ayrıntı paneli yalnız olumsuz oyun İLK adımında açılır; her açılış temiz başlar.
      closePanel()
      if (target === 'down') setAskReason(true)
      else showThanks()
    } catch {
      setCurrent(previous)
      closePanel()
    } finally {
      setBusy(false)
    }
  }

  const trimmedComment = comment.trim()
  const canSubmit = !busy && (reasonCode !== null || trimmedComment.length > 0)

  /** "Gönder": seçili neden ve not tek istekte. Hata → panel açık, metin korunur. */
  const submitDetails = async () => {
    if (!canSubmit) return
    setBusy(true)
    setDetailError(false)
    try {
      await onVote('down', reasonCode ?? undefined, trimmedComment || undefined)
      closePanel()
      showThanks()
    } catch {
      setDetailError(true)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className={askReason ? `${classes.feedbackBar} ${classes.feedbackBarOpen}` : classes.feedbackBar}>
      <div className={classes.feedbackButtons}>
        <button
          type='button'
          aria-label='Bu yanıt işime yaradı'
          aria-pressed={current === 'up'}
          disabled={busy}
          className={current === 'up' ? classes.feedbackActive : undefined}
          onClick={() => send(nextVote(current, 'up'))}
        >
          <span aria-hidden='true'>&#128077;</span>
        </button>
        <button
          type='button'
          aria-label='Bu yanıt işime yaramadı'
          aria-pressed={current === 'down'}
          disabled={busy}
          className={current === 'down' ? classes.feedbackActive : undefined}
          onClick={() => send(nextVote(current, 'down'))}
        >
          <span aria-hidden='true'>&#128078;</span>
        </button>
        {thanks && <span className={classes.feedbackThanks}>Teşekkürler, not alındı.</span>}
      </div>

      {askReason && (
        <div className={classes.feedbackReasons}>
          <div className={classes.feedbackReasonsHead}>
            <span>Nedenini seçer misiniz?</span>
            <button
              type='button'
              className={classes.feedbackClose}
              aria-label='Neden sorusunu kapat'
              onClick={() => {
                closePanel()
                showThanks()
              }}
            >
              &times;
            </button>
          </div>

          <div className={classes.feedbackChips}>
            {FEEDBACK_REASONS.map(reason => (
              <button
                key={reason.code}
                type='button'
                aria-pressed={reasonCode === reason.code}
                disabled={busy}
                className={reasonCode === reason.code ? classes.feedbackChipActive : undefined}
                onClick={() => setReasonCode(prev => (prev === reason.code ? null : reason.code))}
              >
                {reason.label}
              </button>
            ))}
          </div>

          <textarea
            className={classes.feedbackComment}
            value={comment}
            maxLength={FEEDBACK_COMMENT_MAX}
            rows={2}
            placeholder='Eklemek istediğiniz bir not var mı? (isteğe bağlı)'
            aria-label='Geri bildirim notu'
            disabled={busy}
            onChange={e => setComment(e.target.value.slice(0, FEEDBACK_COMMENT_MAX))}
          />

          <div className={classes.feedbackFooter}>
            {detailError && (
              <span className={classes.feedbackError} role='alert'>
                Gönderilemedi, tekrar deneyin.
              </span>
            )}
            <span className={classes.feedbackCounter}>{`${comment.length}/${FEEDBACK_COMMENT_MAX}`}</span>
            <button type='button' className={classes.feedbackSubmit} disabled={!canSubmit} onClick={submitDetails}>
              Gönder
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
